import { Button, Flex, Heading, Stack } from "@knowtex-ai/components";
import { Trash } from "iconoir-react";
import type { ReactElement } from "react";
import type { RecordingStatus } from "./recorderUiRules";

export interface RecorderTransportProps {
  recordingStatus: RecordingStatus;
  canStop: boolean;
  onStop: () => void;
  onTogglePause: () => void;
  onDelete: () => void;
}

const iconButtonStyle = {
  height: "3.5rem",
  width: "3.5rem",
};

export default function RecorderTransport({
  recordingStatus,
  canStop,
  onStop,
  onTogglePause,
  onDelete,
}: RecorderTransportProps): ReactElement | null {
  if (recordingStatus !== "recording" && recordingStatus !== "paused") {
    return null;
  }

  const isPaused = recordingStatus === "paused";
  const stopColor = canStop ? "rgba(185, 28, 28, 1)" : "rgba(150, 150, 150, 1)";

  return (
    <Stack gap={16} className="w-full items-center">
      {isPaused ? (
        <Heading as="h2" size="small" className="text-center text-dark-purple">
          Recording Paused
        </Heading>
      ) : null}
      <Flex gap={12} justifyContent="center">
        <button
          id="stop_button"
          type="button"
          disabled={!canStop}
          aria-label="Stop recording"
          onClick={onStop}
          className="mr-4 flex flex-col items-center text-xl"
          style={{
            color: stopColor,
            cursor: canStop ? "pointer" : "not-allowed",
            opacity: canStop ? 1 : 0.5,
          }}
        >
          <svg
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="currentColor"
            viewBox="0 0 24 24"
            style={{ ...iconButtonStyle, color: stopColor }}
          >
            <path
              fillRule="evenodd"
              d="M2 12a10 10 0 1 1 20 0 10 10 0 0 1-20 0Zm7.7-3.7a1 1 0 0 0-1.4 1.4l2.3 2.3-2.3 2.3a1 1 0 1 0 1.4 1.4l2.3-2.3 2.3 2.3a1 1 0 0 0 1.4-1.4L13.4 12l2.3-2.3a1 1 0 0 0-1.4-1.4L12 10.6 9.7 8.3Z"
              clipRule="evenodd"
            />
          </svg>
          Stop
        </button>
        {isPaused ? (
          <button
            type="button"
            aria-label="Resume recording"
            onClick={onTogglePause}
            className="ml-4 flex flex-col items-center text-xl text-knowtex-grey"
          >
            <svg
              className="text-knowtex-grey"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="currentColor"
              viewBox="0 0 24 24"
              style={iconButtonStyle}
            >
              <path
                fillRule="evenodd"
                d="M17 6a1 1 0 1 0-2 0v4L8.6 5.2A1 1 0 0 0 7 6v12a1 1 0 0 0 1.6.8L15 14v4a1 1 0 1 0 2 0V6Z"
                clipRule="evenodd"
              />
            </svg>
            Resume
          </button>
        ) : (
          <button
            id="pause_button"
            type="button"
            aria-label="Pause recording"
            onClick={onTogglePause}
            className="mr-4 flex flex-col items-center text-xl text-dark-purple"
          >
            <svg
              className="text-dark-purple"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              style={iconButtonStyle}
            >
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M10 9v6m4-6v6m7-3a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
              />
            </svg>
            Pause
          </button>
        )}
      </Flex>
      <Flex justifyContent="center">
        <Button
          type="button"
          variant="text-danger"
          icon={Trash}
          aria-label="Delete audio"
          onClick={onDelete}
        >
          Delete Audio
        </Button>
      </Flex>
    </Stack>
  );
}
